/**
 * useKeyboardShortcuts Hook
 * 
 * Handles global keyboard shortcuts.
 */

import { useCallback,useEffect } from 'react';

import { SHORTCUTS } from '@/lib/constants';

type ShortcutHandler = () => void;

interface ShortcutHandlers {
  onRunPipeline?: ShortcutHandler;
  onFormat?: ShortcutHandler;
  onSavePipeline?: ShortcutHandler;
  onClearInput?: ShortcutHandler;
  onToggleSidebar?: ShortcutHandler;
  onSearchTools?: ShortcutHandler;
}

/**
 * Parse a shortcut string like "mod+enter" into key parts.
 */
function parseShortcut(shortcut: string): { key: string; mod: boolean; shift: boolean; alt: boolean } {
  const parts = shortcut.toLowerCase().split('+');
  return {
    key: parts[parts.length - 1],
    mod: parts.includes('mod'),
    shift: parts.includes('shift'),
    alt: parts.includes('alt'),
  };
}

/**
 * Check if an event matches a shortcut.
 */
function matchesShortcut(event: KeyboardEvent, shortcut: string): boolean {
  const { key, mod, shift, alt } = parseShortcut(shortcut);
  const isMac = navigator.platform.toLowerCase().includes('mac');
  const modKey = isMac ? event.metaKey : event.ctrlKey;

  return (
    event.key.toLowerCase() === key &&
    modKey === mod &&
    event.shiftKey === shift &&
    event.altKey === alt
  );
}

/**
 * Hook to handle keyboard shortcuts.
 */
export function useKeyboardShortcuts(handlers: ShortcutHandlers): void {
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // Don't trigger shortcuts when typing in inputs
      const target = event.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        // Allow some shortcuts even in inputs
        if (!matchesShortcut(event, SHORTCUTS.RUN_PIPELINE)) {
          return;
        }
      }

      // Check each shortcut
      if (handlers.onRunPipeline && matchesShortcut(event, SHORTCUTS.RUN_PIPELINE)) {
        event.preventDefault();
        handlers.onRunPipeline();
      } else if (handlers.onFormat && matchesShortcut(event, SHORTCUTS.FORMAT)) {
        event.preventDefault();
        handlers.onFormat();
      } else if (handlers.onSavePipeline && matchesShortcut(event, SHORTCUTS.SAVE_PIPELINE)) {
        event.preventDefault();
        handlers.onSavePipeline();
      } else if (handlers.onClearInput && matchesShortcut(event, SHORTCUTS.CLEAR_INPUT)) {
        event.preventDefault();
        handlers.onClearInput();
      } else if (handlers.onToggleSidebar && matchesShortcut(event, SHORTCUTS.TOGGLE_SIDEBAR)) {
        event.preventDefault();
        handlers.onToggleSidebar();
      } else if (handlers.onSearchTools && matchesShortcut(event, SHORTCUTS.SEARCH_TOOLS)) {
        event.preventDefault();
        handlers.onSearchTools();
      }
    },
    [handlers]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);
}

